import React, { useState, useMemo } from 'react';
import PersonCard from './PersonCard';

const CLASS_TYPES = [
  { key: 'discovery_101', label: 'Discovery 101', dateField: 'discovery_date', hostField: 'table_host_discovery', className: 'discovery' },
  { key: 'serve_201', label: 'Serve 201', dateField: 'serve_date', hostField: 'table_host_serve', className: 'serve' },
  { key: 'values_301', label: 'Values 301', dateField: 'values_date', hostField: 'table_host_values', className: 'values' },
];

function formatDate(dateStr) {
  if (!dateStr) return '';
  const clean = dateStr.split('T')[0];
  const parts = clean.includes('/') ? clean.split('/') : clean.split('-');
  let d;
  if (parts[0].length === 4) {
    d = new Date(parts[0], parts[1] - 1, parts[2]);
  } else {
    d = new Date(parts[2], parts[0] - 1, parts[1]);
  }
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function parseDateForSort(dateStr) {
  if (!dateStr) return null;
  const clean = dateStr.split('T')[0];
  const parts = clean.includes('/') ? clean.split('/') : clean.split('-');
  if (parts[0].length === 4) {
    return new Date(parts[0], parts[1] - 1, parts[2]);
  }
  return new Date(parts[2], parts[0] - 1, parts[1]);
}

function ClassView({ allPeople, onPersonClick }) {
  const [selectedClass, setSelectedClass] = useState('discovery_101');
  const [selectedDate, setSelectedDate] = useState(null);

  const classType = CLASS_TYPES.find(c => c.key === selectedClass);

  // Get all unique dates for the selected class, sorted newest-first
  const availableDates = useMemo(() => {
    const dateSet = new Map();
    for (const p of allPeople) {
      const dateVal = p[classType.dateField];
      if (dateVal) {
        const parsed = parseDateForSort(dateVal);
        const key = parsed ? parsed.toISOString().split('T')[0] : dateVal;
        if (!dateSet.has(key)) {
          dateSet.set(key, { raw: dateVal, parsed, key });
        }
      }
    }
    return Array.from(dateSet.values()).sort((a, b) => (b.parsed || 0) - (a.parsed || 0));
  }, [allPeople, classType.dateField]);

  // Reset selected date when switching class type if it's not valid
  const activeDate = availableDates.find(d => d.key === selectedDate) ? selectedDate : null;

  // Filter people for selected date, group by table host
  const hostGroups = useMemo(() => {
    if (!activeDate) return [];

    const filtered = allPeople.filter(p => {
      const dateVal = p[classType.dateField];
      if (!dateVal) return false;
      const parsed = parseDateForSort(dateVal);
      const key = parsed ? parsed.toISOString().split('T')[0] : dateVal;
      return key === activeDate;
    });

    const groups = new Map();
    for (const p of filtered) {
      const host = p[classType.hostField] || 'Unassigned';
      if (!groups.has(host)) groups.set(host, []);
      groups.get(host).push(p);
    }

    // Sort hosts alphabetically, but put "Unassigned" last
    const sorted = Array.from(groups.entries()).sort((a, b) => {
      if (a[0] === 'Unassigned') return 1;
      if (b[0] === 'Unassigned') return -1;
      return a[0].localeCompare(b[0]);
    });

    // Sort people within each group by last name
    for (const [, people] of sorted) {
      people.sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''));
    }

    return sorted;
  }, [allPeople, activeDate, classType]);

  const totalPeople = hostGroups.reduce((sum, [, people]) => sum + people.length, 0);

  return (
    <div className="class-view">
      <div className="class-view-controls">
        <div className="class-type-tabs">
          {CLASS_TYPES.map(ct => (
            <button
              key={ct.key}
              className={`class-type-tab ${ct.className}${selectedClass === ct.key ? ' active' : ''}`}
              onClick={() => { setSelectedClass(ct.key); setSelectedDate(null); }}
            >
              {ct.label}
            </button>
          ))}
        </div>

        {availableDates.length > 0 && (
          <div className="class-date-selector">
            <select
              value={activeDate || ''}
              onChange={e => setSelectedDate(e.target.value || null)}
            >
              <option value="">Select a class date...</option>
              {availableDates.map(d => (
                <option key={d.key} value={d.key}>{formatDate(d.raw)}</option>
              ))}
            </select>
          </div>
        )}

        {activeDate && (
          <span className="class-view-count">
            {totalPeople} {totalPeople === 1 ? 'person' : 'people'}
          </span>
        )}
      </div>

      {!activeDate && availableDates.length > 0 && (
        <div className="class-view-empty">
          Select a class date to view table assignments
        </div>
      )}

      {availableDates.length === 0 && (
        <div className="class-view-empty">
          No {classType.label} dates found
        </div>
      )}

      {activeDate && hostGroups.length > 0 && (
        <div className="host-groups">
          {hostGroups.map(([hostName, people]) => (
            <div className="host-group" key={hostName}>
              <div className="host-group-header">
                <span className={`host-name-tag ${classType.className}`}>{hostName}</span>
                <span className="host-group-count">
                  {people.length} {people.length === 1 ? 'person' : 'people'}
                </span>
              </div>
              <div className="section-divider" />
              <div className="people-grid">
                {people.map(person => (
                  <PersonCard
                    key={person.id}
                    person={person}
                    sectionClass={classType.className}
                    onClick={() => onPersonClick(person)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { ClassView };
